"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, IconButton, Menu } from '@chakra-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Flash } from 'iconoir-react';
import { $createTextNode, $getSelection, $insertNodes, $isRangeSelection, } from 'lexical';
import { useMemo } from 'react';
import { $createVariableNode } from '../nodes/variable-node';
import { VariableOptionItem } from './variable-option-item';
/**
 * Resolve an optional i18n key to display text, falling back to the English
 * literal when the key has no translation.
 *
 * i18next returns the key itself for a missing translation, so a return value
 * equal to the key (or empty) means "not translated" and the literal must win.
 * Without this guard a registry entry that gains a `labelKey` before the locale
 * files catch up paints a raw key like `invitations.variables.collab_name.label`
 * in the dropdown and typeahead.
 */
export function resolveTranslatedText(fallback, key, translateFn) {
    if (!key || !translateFn)
        return fallback;
    const translated = translateFn(key);
    return !translated || translated === key ? fallback : translated;
}
/** Resolve an AvailableVariable to its display-ready form (translated label/description + disabled state). */
export function resolveVariable(variable, translateFn) {
    const description = resolveTranslatedText(variable.description ?? '', variable.descriptionKey, translateFn);
    return {
        key: variable.key,
        label: resolveTranslatedText(variable.label, variable.labelKey, translateFn),
        description: description || undefined,
        disabled: variable.disabled || !variable.value || variable.value.trim() === '',
        disabledReason: variable.disabledReason,
    };
}
export function VariableDropdownPlugin({ availableVariables, translateFn, insertVariableLabel = 'Insert variable', placement = 'bottom-start', compact = false, insertTrailingSpace = true, }) {
    const [editor] = useLexicalComposerContext();
    const resolvedVariables = useMemo(() => availableVariables.map((v) => resolveVariable(v, translateFn)), [availableVariables, translateFn]);
    const handleInsert = (variableKey) => {
        const item = resolvedVariables.find((v) => v.key === variableKey);
        if (item?.disabled)
            return;
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const nodes = [$createVariableNode(variableKey)];
                if (insertTrailingSpace)
                    nodes.push($createTextNode(' '));
                $insertNodes(nodes);
            }
        });
        editor.focus();
    };
    return (_jsxs(Menu.Root, { positioning: { placement }, children: [_jsx(Menu.Trigger, { asChild: true, children: compact ? (_jsx(IconButton, { size: "xs", variant: "ghost", colorPalette: "gray", flexShrink: 0, "aria-label": insertVariableLabel, children: _jsx(Flash, { width: 14, height: 14 }) })) : (_jsxs(Button, { size: "xs", variant: "subtle", colorPalette: "gray", flexShrink: 0, children: [_jsx(Flash, { width: 14, height: 14 }), insertVariableLabel] })) }), _jsx(Menu.Positioner, { children: _jsx(Menu.Content, { minW: "240px", children: resolvedVariables.map((variable) => (_jsx(Menu.Item, { value: variable.key, onClick: () => handleInsert(variable.key), py: 1, px: 2, cursor: variable.disabled ? 'not-allowed' : 'pointer', children: _jsx(VariableOptionItem, { label: variable.label, variableKey: variable.key, isDisabled: variable.disabled, disabledReason: variable.disabledReason }) }, variable.key))) }) })] }));
}
